import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Card from "@pathwright/ui/src/components/card/Card"
import CardHeader from "@pathwright/ui/src/components/card/CardHeader"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import styled from "styled-components"
import {
  OrderFragment,
  useUserProfileRegistrationsQuery
} from "@pathwright/graphql-client/src/generated"
import { getGroupUrl, getStepUrl } from "../utils/urls"
import OrderProcessing, { OrderProcessingLoading } from "./OrderProcessing"
import { OnOrderFulfilledCallback } from "./useOrderPollingQuery"

const NarrowCard = styled(Card)`
  & > .View {
    max-width: 500px;
  }
`

function useOrderRegistration(order: OrderFragment) {
  const userRegistrationQuery = useUserProfileRegistrationsQuery({
    variables: {
      first: 1,
      id: order.user_id,
      id_filter: {
        eq: order.fulfilled_object_id
      }
    },
    skip: !order.fulfilled_object_id
  })

  return (
    userRegistrationQuery.data?.user?.registrations?.edges?.[0]?.node || null
  )
}

export const RegistrationOrderConfirmationContent = ({
  order
}: {
  order: OrderFragment
}) => {
  const { t } = useTranslate()
  const registration = useOrderRegistration(order)

  return registration ? (
    <BlankSlate
      animateIcon
      stack
      icons={null}
      icon="check"
      heading={t("You’re Registered!")}
      body={
        <ul style={{ textAlign: "left", maxWidth: 350 }}>
          <li>{t("You now have 24/7 access to this course")}.</li>
          <li>
            {t(
              `We've sent you an email receipt with your order information for your records`
            )}
            .
          </li>
        </ul>
      }
      primaryAction={{
        // Hacky method of ensuring we navigate back to the current step
        // if user registered from a step. Better method would be to return
        // to the previous url if url contains "/path/". This will be easier done
        // once s2-inbox is merged which has a simple hook for getting the prev url.
        to: window.App?.getStore("step").get("step")
          ? getStepUrl(
              registration.resource?.slug || "",
              registration.group?.id,
              window.App.getStore("step").get("step").get("sync_source_id")
            )
          : getGroupUrl(
              registration.resource?.slug || "",
              registration.group?.id,
              "path"
            ),
        children: t("Get Started")
      }}
    />
  ) : (
    <OrderProcessingLoading />
  )
}

type RegistrationOrderConfirmationProps = {
  orderId: number
  onOrderFulfilled?: OnOrderFulfilledCallback
}

export const RegistrationOrderConfirmation = ({
  orderId,
  onOrderFulfilled
}: RegistrationOrderConfirmationProps) => {
  return (
    <OrderProcessing orderId={orderId} onOrderFulfilled={onOrderFulfilled}>
      {({ order }) => <RegistrationOrderConfirmationContent order={order} />}
    </OrderProcessing>
  )
}

function RegistrationOrderConfirmationCardHeader({
  order,
  card
}: {
  order: OrderFragment
  card: any
}) {
  const registration = useOrderRegistration(order)

  return (
    <CardHeader
      card={card}
      resource={registration?.resource}
      title={registration?.resource?.name || ""}
      meta={registration?.group?.name || ""}
    />
  )
}

type RegistrationOrderConfirmationCardProps = {
  card: any
  orderId: number
  onOrderFulfilled?: OnOrderFulfilledCallback
}

const RegistrationOrderConfirmationCard = ({
  card,
  orderId,
  onOrderFulfilled
}: RegistrationOrderConfirmationCardProps) => {
  return (
    <NarrowCard card={card} navless>
      <OrderProcessing orderId={orderId} onOrderFulfilled={onOrderFulfilled}>
        {({ order }) => (
          <>
            <RegistrationOrderConfirmationCardHeader
              order={order}
              card={card}
            />
            <RegistrationOrderConfirmationContent order={order} />
          </>
        )}
      </OrderProcessing>
    </NarrowCard>
  )
}

export default RegistrationOrderConfirmationCard
